<script lang="ts" setup>
import dayjs from "dayjs";
import "dayjs/locale/de";
import "dayjs/locale/en";
import "dayjs/locale/es";
import "dayjs/locale/fr";
import "dayjs/locale/it";
import { useConfigStore } from "~/core/stores/useConfigStore";
import { useConfirmLeavePageStore } from "~/core/stores/useConfirmLeavePageStore";
import { useUserSession } from "~/modules/user/stores/useUserSession";
import { loadLocale } from "~/plugins/i18n";
import { PATH_CARTOGRAPHY } from "./modules/cartography/constants";

const { getConfig } = useConfigStore();

const route = useRoute();
const { locale, t } = useI18n();
const userSessionStore = useUserSession();

const shouldVersionBeDisplayed = computed(() => route.path != PATH_CARTOGRAPHY);

const optionalLabel = computed(() => t("input.optional"));
provide("lxc-optional-label", optionalLabel);

const updateLanguage = () => {
  const currentLocale = userSessionStore.userSession?.language ?? locale.value;
  loadLocale(currentLocale);
  dayjs.locale(currentLocale);
};

/**
 * Start managing display confirm message on closing/reload window and on leaving page
 */
const confirmLeavePageStore = useConfirmLeavePageStore();

// Display confirm message on refresh/close window
window.addEventListener("beforeunload", (event) => {
  if (confirmLeavePageStore.isActivated()) {
    event.returnValue = confirmLeavePageStore.messageConfirmUnsavedData;
  }
});

// show confirm message component based on the confirmLeavePageStore "shown" state
const showConfirm = computed({
  get(): boolean {
    return confirmLeavePageStore.$state.shown;
  },
  set(showConfirm: boolean) {
    confirmLeavePageStore.$state.shown = showConfirm;
  },
});
/**
 * End managing display confirm message on closing/reload window and on leaving page
 */

// Init after user session has changed
watch(
  () => userSessionStore.userSession,
  () => {
    // Case when the user is authenticated
    if (userSessionStore.isAuthenticated) {
      updateLanguage();
    }
  },
);

const onCancelLeavePageconfirm = () => {
  confirmLeavePageStore.cancel();
  showConfirm.value = false;
};

// Warning: the user can be not already authenticated here
onBeforeMount(updateLanguage);
onMounted(getConfig);
</script>

<template>
  <router-view />
  <version-component v-if="shouldVersionBeDisplayed" />

  <!-- Leave page confirm component -->
  <confirm-modal
    :is-dialog-visible="showConfirm"
    :title="t(confirmLeavePageStore.title)"
    :description="t(confirmLeavePageStore.messageConfirmUnsavedData)"
    :ok-label="t('message.confirm.okLabel')"
    :cancel-label="t('message.confirm.cancelLabel')"
    @confirm="confirmLeavePageStore.confirm"
    @cancel="onCancelLeavePageconfirm"
  />
</template>
